import { useEffect } from "react";
import { Navigate, Outlet, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useMe } from "@/lib/queries";
import { applyBrandingToDocument } from "@/lib/branding";
import { BottomNav } from "@/components/BottomNav";
import { Spinner } from "@/components/Spinner";

/**
 * Casca autenticada do portal. Faz três guardas, na ordem:
 *  1. sessão Supabase carregando → spinner;
 *  2. sem sessão → manda pro /login (guardando o destino em redirectedFrom);
 *  3. sessão OK mas o slug da URL não é o do aluno logado → redireciona pro
 *     slug correto (anti-impersonation client-side; o Worker valida ownership
 *     via RLS de qualquer forma).
 * Resolvidas as guardas, aplica o branding do trainer e renderiza a aba ativa
 * (<Outlet/>) sobre o BottomNav.
 */
export function AppShell() {
  const { slug = "" } = useParams();
  const location = useLocation();
  const { session, loading } = useAuth();
  const me = useMe();

  useEffect(() => {
    if (me.data) {
      applyBrandingToDocument({ trainer: me.data.trainer });
    }
  }, [me.data]);

  if (loading) return <Spinner fullscreen />;

  if (!session) {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/s/${slug}/login?redirectedFrom=${from}`} replace />;
  }

  if (me.isLoading) return <Spinner fullscreen />;

  // Sessão morta no servidor (token revogado) ou slug de outro aluno.
  if (me.isError) {
    return <Navigate to={`/s/${slug}/login`} replace />;
  }
  if (me.data && me.data.short_slug !== slug) {
    return <Navigate to={`/s/${me.data.short_slug}`} replace />;
  }

  return (
    <div className="mx-auto min-h-dvh max-w-lg pb-24">
      <Outlet />
      <BottomNav slug={slug} />
    </div>
  );
}
